import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useLiveAvatarContextOptional } from './LiveAvatar.context';

/**
 * LiveAvatar
 *
 * Renders the HeyGen avatar as a <video> element. Attaches the LiveKit video
 * stream to the element once SESSION_STREAM_READY fires.
 *
 * Uses useLiveAvatarContextOptional so it silently renders nothing while the
 * session token is still loading (before LiveAvatarContextProvider is mounted).
 *
 * Must be rendered as a descendant of LiveAvatarSessionManager.
 */
export function LiveAvatar(): React.ReactElement | null {
  const ctx = useLiveAvatarContextOptional();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!ctx?.isStreamReady || !ctx.sessionRef.current || !videoRef.current) {
      return;
    }
    // Attach the LiveKit MediaStream to the video element once the stream is ready.
    // After attach(), the avatar video begins playing automatically (autoPlay).
    ctx.sessionRef.current.attach(videoRef.current);
  }, [ctx?.isStreamReady, ctx?.sessionRef]);

  // Sync the muted state from context to the video element imperatively.
  // We use a separate effect so attach() and mute can be reasoned about independently.
  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.muted = ctx?.isMuted ?? false;
  }, [ctx?.isMuted]);

  // No context = session not yet started. Render nothing so the panel
  // shows whatever placeholder HeyGenPanel provides behind it.
  if (ctx === null) {
    return null;
  }

  return (
    <VideoElement
      ref={videoRef}
      autoPlay
      playsInline
      aria-label="HeyGen Digital Twin Avatar"
    />
  );
}

const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
`;
